import { create } from 'zustand'

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading' | 'alarm'

export interface Toast {
  id: string
  type: ToastType
  text: string
  duration?: number
  timeoutId?: ReturnType<typeof setTimeout>
  onClick?: () => void
  /**
   * 뒤이어 다른 토스트가 떠도 2초로 단축되지 않는다.
   * 사용자가 조치해야만 벗어날 수 있는 상태(예: 세션 복구 실패)를 알릴 때만 쓴다 —
   * 그런 안내가 다른 토스트에 밀려 사라지면 사용자는 이유를 모른 채 갇힌다.
   */
  sticky?: boolean
}

interface ToastStore {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  clearAllToasts: () => void
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  addToast: (toast) => {
    set((state) => {
      const sameIndex = state.toasts.findIndex(
        (t) => t.type === toast.type && t.text === toast.text
      )

      // If same toast exists => refresh its timer
      if (sameIndex !== -1) {
        const existing = state.toasts[sameIndex]

        // clear old timer
        if (existing.timeoutId) clearTimeout(existing.timeoutId)

        const duration = toast.duration || 5000
        const newTimeout = setTimeout(() => {
          useToastStore.getState().removeToast(existing.id)
        }, duration)

        const updated = { ...existing, duration, timeoutId: newTimeout }
        const newToasts = [...state.toasts]
        newToasts[sameIndex] = updated

        return { toasts: newToasts }
      }

      // Shorten current toasts to 2s (sticky 토스트는 제외 — 위 sticky 주석 참조)
      const shortenedToasts = state.toasts.map((t) => {
        if (t.sticky) return t
        if (t.timeoutId) clearTimeout(t.timeoutId)
        const newTimeout = setTimeout(() => {
          useToastStore.getState().removeToast(t.id)
        }, 2000)
        return { ...t, timeoutId: newTimeout, duration: 2000 }
      })

      // Add new toast
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      const duration = toast.duration || 5000
      const timeoutId = setTimeout(() => {
        useToastStore.getState().removeToast(id)
      }, duration)

      const newToast: Toast = { ...toast, id, duration, timeoutId }

      return { toasts: [...shortenedToasts, newToast] }
    })
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    })),

  clearAllToasts: () =>
    set(() => ({
      toasts: [],
    })),
}))
